import { z } from "zod";
import { businessUnitTypeSchema } from "../auth/features.js";
import { pageOf, pageQuerySchema } from "../common/pagination.js";
import { patchOf } from "../common/patch.js";
import { localDateSchema, timestampSchema, uuidSchema } from "../common/primitives.js";
const optionalText = (max) => z.string().trim().max(max).nullable().default(null);
// ── Şirket ───────────────────────────────────────────────────────────────────
export const companyTypeSchema = z.enum(["SOLE_PROPRIETORSHIP", "LIMITED", "JOINT_STOCK", "OTHER"]);
export const COMPANY_TYPE_LABELS = {
    SOLE_PROPRIETORSHIP: "Şahıs şirketi",
    LIMITED: "Limited şirket",
    JOINT_STOCK: "Anonim şirket",
    OTHER: "Diğer",
};
export const companySchema = z.object({
    id: uuidSchema,
    legalName: z.string(),
    shortName: z.string().nullable(),
    companyType: companyTypeSchema,
    taxId: z.string().describe("VKN (10 hane) ya da şahıs şirketinde TCKN (11 hane)"),
    taxIdKind: z.enum(["VKN", "TCKN"]),
    taxOffice: z.string().nullable(),
    mersisNo: z.string().nullable(),
    tradeRegistryNo: z.string().nullable(),
    city: z.string().nullable(),
    district: z.string().nullable(),
    address: z.string().nullable(),
    isEInvoiceRegistered: z.boolean(),
    defaultCurrency: z.literal("TRY"),
    isActive: z.boolean(),
    version: z.number().int(),
    createdAt: timestampSchema,
});
/** VKN/TCKN kontrol hanesi sunucuda `@station/core` ile doğrulanır; burada yalnızca biçim denetlenir. */
export const createCompanySchema = z.object({
    legalName: z.string().trim().min(1, "Ünvan zorunludur").max(300),
    shortName: optionalText(100),
    companyType: companyTypeSchema,
    taxId: z
        .string()
        .trim()
        .regex(/^\d{10,11}$/, "VKN 10, TCKN 11 haneli olmalıdır"),
    taxOffice: optionalText(100),
    mersisNo: z
        .string()
        .trim()
        .regex(/^\d{16}$/, "MERSİS no 16 haneli olmalıdır")
        .nullable()
        .default(null),
    tradeRegistryNo: optionalText(50),
    city: optionalText(60),
    district: optionalText(60),
    address: optionalText(500),
    isEInvoiceRegistered: z.boolean().default(false),
});
export const updateCompanySchema = patchOf(createCompanySchema).extend({
    isActive: z.boolean().optional(),
    version: z.number().int().positive(),
});
export const companyListSchema = z.object({ items: z.array(companySchema) });
// ── Bölge ────────────────────────────────────────────────────────────────────
export const regionSchema = z.object({
    id: uuidSchema,
    name: z.string(),
    isActive: z.boolean(),
    stationCount: z.number().int(),
});
export const regionListSchema = z.object({ items: z.array(regionSchema) });
export const createRegionSchema = z.object({
    name: z.string().trim().min(1, "Bölge adı zorunludur").max(120),
});
export const updateRegionSchema = z.object({
    name: z.string().trim().min(1).max(120).optional(),
    isActive: z.boolean().optional(),
});
// ── İstasyon ─────────────────────────────────────────────────────────────────
export const stationStatusSchema = z.enum(["ACTIVE", "INACTIVE", "TRANSFERRED_OUT"]);
export const businessUnitSchema = z.object({
    unitType: businessUnitTypeSchema,
    isActive: z.boolean(),
    startedOn: localDateSchema,
    /** m² — gideri alana göre dağıtan anahtarlarda ağırlık olarak kullanılır (Prompt 11). */
    areaM2: z.string().nullable(),
});
export const stationSchema = z.object({
    id: uuidSchema,
    companyId: uuidSchema,
    companyName: z.string(),
    regionId: uuidSchema.nullable(),
    regionName: z.string().nullable(),
    code: z.string(),
    name: z.string(),
    city: z.string().nullable(),
    district: z.string().nullable(),
    address: z.string().nullable(),
    latitude: z.string().nullable().describe("Ondalık derece (dizge)"),
    longitude: z.string().nullable(),
    distributorId: uuidSchema.nullable(),
    distributorName: z.string().nullable(),
    epdkLicenseNo: z.string().nullable(),
    epdkLicenseExpiresOn: localDateSchema.nullable(),
    phone: z.string().nullable(),
    status: stationStatusSchema,
    activeFrom: localDateSchema,
    activeTo: localDateSchema.nullable(),
    transferredFromStationId: uuidSchema.nullable(),
    businessUnits: z.array(businessUnitSchema),
    version: z.number().int(),
    createdAt: timestampSchema,
});
export const stationPageSchema = pageOf(stationSchema);
const coordinate = (min, max, label) => z
    .string()
    .trim()
    .regex(/^-?\d{1,3}(\.\d{1,6})?$/, `${label} ondalık derece olmalıdır (en fazla 6 basamak)`)
    .refine((value) => Number(value) >= min && Number(value) <= max, `${label} ${min} ile ${max} arasında olmalıdır`)
    .nullable()
    .default(null);
export const stationCodeSchema = z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z0-9][A-Z0-9_-]{0,19}$/, "Kod 1–20 karakter; büyük harf, rakam, - ve _ içerebilir");
export const createStationSchema = z.object({
    companyId: uuidSchema,
    regionId: uuidSchema.nullable().default(null),
    code: stationCodeSchema,
    name: z.string().trim().min(1, "İstasyon adı zorunludur").max(200),
    city: optionalText(60),
    district: optionalText(60),
    address: optionalText(500),
    latitude: coordinate(-90, 90, "Enlem"),
    longitude: coordinate(-180, 180, "Boylam"),
    distributorId: uuidSchema.nullable().default(null),
    epdkLicenseNo: optionalText(50),
    epdkLicenseExpiresOn: localDateSchema.nullable().default(null),
    phone: optionalText(30),
    activeFrom: localDateSchema.optional(),
    businessUnits: z.array(businessUnitTypeSchema).default(["FUEL"]),
});
export const updateStationSchema = patchOf(createStationSchema.omit({ businessUnits: true, companyId: true })).extend({ status: z.enum(["ACTIVE", "INACTIVE"]).optional(), version: z.number().int().positive() });
export const listStationsQuerySchema = pageQuerySchema.extend({
    companyId: uuidSchema.optional(),
    regionId: uuidSchema.optional(),
    status: stationStatusSchema.optional(),
    search: z.string().trim().max(100).optional(),
});
export const setBusinessUnitsSchema = z.object({
    units: z
        .array(z.object({
        unitType: businessUnitTypeSchema,
        isActive: z.boolean(),
        /** m² (isteğe bağlı); gönderilmezse mevcut değer KORUNUR, null gönderilirse temizlenir. */
        areaM2: z
            .string()
            .regex(/^\d{1,10}(\.\d{1,2})?$/, "Alan negatif olmayan bir sayı dizgesi olmalıdır")
            .refine((value) => Number(value) > 0, "Alan sıfırdan büyük olmalıdır")
            .nullable()
            .optional(),
    }))
        .max(7),
});
export const idParamsSchema = z.object({ id: uuidSchema });
// ── Dağıtıcı kataloğu ────────────────────────────────────────────────────────
export const distributorSchema = z.object({ id: uuidSchema, name: z.string(), shortName: z.string() });
export const distributorListSchema = z.object({ items: z.array(distributorSchema) });
// ── Toplu istasyon içe aktarma ───────────────────────────────────────────────
/** Şablon sütunları (başlık satırı). Sıra bağımsızdır; başlıklar birebir eşleşmelidir. */
export const STATION_IMPORT_COLUMNS = [
    "kod",
    "ad",
    "sirket_vergi_no",
    "bolge",
    "il",
    "ilce",
    "adres",
    "enlem",
    "boylam",
    "dagitici",
    "epdk_lisans_no",
    "epdk_lisans_bitis",
    "telefon",
    "is_birimleri",
];
export const stationImportRequestSchema = z.object({ fileId: uuidSchema });
export const stationImportRowSchema = z.object({
    rowNumber: z.number().int().describe("Dosyadaki satır numarası (başlık = 1)"),
    code: z.string(),
    name: z.string(),
    valid: z.boolean(),
    errors: z.array(z.object({ column: z.string(), message: z.string() })),
});
export const stationImportPreviewSchema = z.object({
    fileId: uuidSchema,
    totalRows: z.number().int(),
    validRows: z.number().int(),
    invalidRows: z.number().int(),
    /** Dosyada olup kiracıda bulunmayan bölgeler; onayda kendiliğinden oluşturulur. */
    newRegions: z.array(z.string()),
    rows: z.array(stationImportRowSchema),
});
export const stationImportResultSchema = z.object({
    created: z.number().int(),
    regionsCreated: z.number().int(),
});
//# sourceMappingURL=org.js.map